import { useEffect, useMemo, useState } from 'react';
import { Flame, GripVertical, Pencil, Plus, Snowflake } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useHabits } from '../stores/habits.js';
import { useToast } from '../stores/toast.js';
import { useUi } from '../stores/ui.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Button } from '../ui/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import {
  todayInParis,
  addDays,
  dayOfWeekShort,
  dayOfMonth,
  isoWeekNumber,
  mondayOfWeek,
  formatFR,
  formatWeekRange,
} from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { cn } from '../lib/cn.js';
import { currentStreak, currentMissStreak } from '../lib/habitStats.js';
import { TrackerOverviewChart } from '../components/TrackerOverviewChart.js';
import type { Habit } from '../api/habits.js';
import { DEFAULT_HABIT_COLOR } from '../lib/palette.js';
import { teinte } from '../lib/teinte.js';
import { WeekNav } from '../components/WeekNav.js';
import { HabitGlyph } from '../components/HabitGlyph.js';
import { ListSkeleton } from '../components/TaskList.js';

export function Tracker(): React.ReactElement {
  const habits = useHabits((s) => s.habits);
  const logs = useHabits((s) => s.logs);
  // Une relecture (retour sur la vue) garde les habitudes déjà là au lieu de les escamoter.
  const pending = useHabits((s) => s.loading && s.habits.length === 0);
  const load = useHabits((s) => s.load);
  const reorder = useHabits((s) => s.reorder);
  const setDay = useHabits((s) => s.setDay);
  const showToast = useToast((s) => s.show);
  const openHabitForm = useUi((s) => s.openHabitForm);
  const isMobile = useIsMobile();
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayBusy, setDayBusy] = useState(false);

  const doneSet = useMemo(
    () => new Set(logs.map((l) => `${l.habitId}|${l.date}`)),
    [logs]
  );
  const isDone = (hid: string, date: string): boolean =>
    doneSet.has(`${hid}|${date}`);

  useEffect(() => {
    load();
  }, [load]);

  const today = todayInParis();
  const currentWeekMonday = mondayOfWeek(today);
  const displayedMonday = addDays(currentWeekMonday, weekOffset * 7);
  const weekNum = isoWeekNumber(displayedMonday);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(displayedMonday, i)),
    [displayedMonday]
  );

  const doneToday = habits.filter((h) => isDone(h.habitId, today)).length;
  const allDone = habits.length > 0 && doneToday === habits.length;

  // Completes what is missing (or clears everything at 11/11); the toast's Undo
  // reverts only the logs this action wrote, never the ones ticked by hand before.
  async function onSetToday(): Promise<void> {
    const done = !allDone;
    setDayBusy(true);
    try {
      const { written, failed } = await setDay(
        today,
        habits.map((h) => h.habitId),
        done
      );
      const verb = done ? 'checked' : 'unchecked';
      if (failed > 0) {
        showToast({ message: `${failed} habit${failed === 1 ? '' : 's'} could not be ${verb}` });
      } else if (written.length > 0) {
        showToast({
          message: `${written.length} habit${written.length === 1 ? '' : 's'} ${verb}`,
          action: {
            label: 'Undo',
            run: async () => {
              await setDay(today, written, !done);
            },
          },
        });
      }
    } finally {
      setDayBusy(false);
    }
  }

  const dayActions = (
    <DayActions
      done={doneToday}
      total={habits.length}
      busy={dayBusy}
      mobile={isMobile}
      onClick={() => void onSetToday()}
    />
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: isMobile ? { delay: 250, tolerance: 5 } : { distance: 5 },
    })
  );

  function onDragEnd(e: DragEndEvent): void {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIdx = habits.findIndex((h) => h.habitId === e.active.id);
    const newIdx = habits.findIndex((h) => h.habitId === e.over!.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(habits, oldIdx, newIdx);
    reorder(reordered.map((h) => h.habitId));
  }

  const weekNav = (
    <WeekNav
      weekNum={weekNum}
      isCurrent={weekOffset === 0}
      onPrevious={() => setWeekOffset((w) => w - 1)}
      onCurrent={() => setWeekOffset(0)}
      onNext={() => setWeekOffset((w) => Math.min(w + 1, 0))}
      nextDisabled={weekOffset >= 0}
    />
  );

  const surtitre = `${formatWeekRange(displayedMonday)} · ${doneToday}/${habits.length} today`;

  if (isMobile) {
    return (
      <div className="px-4 py-5 space-y-6">
        <PageHeader title="Tracker" surtitre={surtitre} />
        {pending ? (
          <TrackerSkeleton />
        ) : habits.length === 0 ? (
          <EmptyState>
            No habits yet. Tap <em>+</em> to start.
          </EmptyState>
        ) : (
          <>
            <TrackerOverviewChart />
            <section>
              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="mention">
                  Today · {dayOfWeekShort(today)} {formatFR(today).slice(0, 5)}
                </h2>
                {dayActions}
              </div>
              <div className="liste">
                {habits.map((h) => (
                  <MobileTodayRow key={h.habitId} habit={h} today={today} isDone={isDone} />
                ))}
              </div>
            </section>

            <section>
              <h2 className="mention mb-2">This week</h2>
              <MiniWeekGrid habits={habits} days={days} today={today} isDone={isDone} />
              <div className="mt-3 flex justify-center">{weekNav}</div>
            </section>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-6 py-8 space-y-5">
      <PageHeader
        title="Tracker"
        surtitre={surtitre}
        actions={
          <>
            {weekNav}
            <Button onClick={() => openHabitForm()}>
              <Plus size={16} aria-hidden />
              New habit
            </Button>
          </>
        }
      />

      {pending ? (
        <TrackerSkeleton />
      ) : habits.length === 0 ? (
        <EmptyState>
          No habits yet. Click <em>New habit</em> to start.
        </EmptyState>
      ) : (
        <>
          <TrackerOverviewChart />
          <DaysHeader days={days} today={today}>
            {dayActions}
          </DaysHeader>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={habits.map((h) => h.habitId)}
              strategy={verticalListSortingStrategy}
            >
              <div className="liste">
                {habits.map((h) => (
                  <HabitRow key={h.habitId} habit={h} days={days} today={today} isDone={isDone} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}

/** La carte du graphique puis les lignes d'habitudes, aux hauteurs du contenu. */
function TrackerSkeleton(): React.ReactElement {
  return (
    <div className="pile" role="status" aria-label="Loading habits">
      <div className="squelette h-32" />
      <ListSkeleton />
    </div>
  );
}

function MobileTodayRow({
  habit,
  today,
  isDone,
}: {
  habit: Habit;
  today: string;
  isDone: (hid: string, date: string) => boolean;
}): React.ReactElement {
  const toggle = useHabits((s) => s.toggle);
  const openHabitStats = useUi((s) => s.openHabitStats);
  const done = isDone(habit.habitId, today);

  return (
    <div className="ligne px-0">
      <button
        type="button"
        onClick={() => openHabitStats(habit.habitId)}
        className="ligne-corps flex items-center gap-3 text-left"
        aria-label={`${habit.name} stats`}
      >
        <HabitGlyph habit={habit} />
        <span className="truncate">{habit.name}</span>
      </button>
      <button
        type="button"
        onClick={() => toggle(habit.habitId, today)}
        aria-pressed={done}
        aria-label={done ? `${habit.name} — mark not done` : `${habit.name} — mark done`}
        className="case case-ronde"
        style={teinte(habit.color ?? DEFAULT_HABIT_COLOR)}
      />
    </div>
  );
}

function MiniWeekGrid({
  habits,
  days,
  today,
  isDone,
}: {
  habits: Habit[];
  days: string[];
  today: string;
  isDone: (hid: string, date: string) => boolean;
}): React.ReactElement {
  const toggle = useHabits((s) => s.toggle);
  return (
    <div className="carte p-2">
      <div className="grid grid-cols-[1fr_repeat(7,auto)] gap-1 items-center">
        <div />
        {days.map((d) => (
          <DayHead key={d} day={d} isToday={d === today} className="w-8" />
        ))}
        {habits.map((h) => (
          <div key={h.habitId} className="contents" style={teinte(h.color ?? DEFAULT_HABIT_COLOR)}>
            <div className="discret flex items-center gap-1.5 truncate py-1 pr-2">
              <span className="shrink-0" aria-hidden>
                {h.emoji || h.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="truncate">{h.name}</span>
            </div>
            {days.map((d) => {
              const done = isDone(h.habitId, d);
              return (
                <button
                  key={`${h.habitId}-${d}`}
                  type="button"
                  onClick={() => toggle(h.habitId, d)}
                  className="case"
                  aria-pressed={done}
                  disabled={d > today}
                  aria-current={d === today ? 'date' : undefined}
                  aria-label={`${h.name}, ${d}, ${done ? 'done' : 'not done'}`}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** L'initiale du jour et son quantième, au-dessus d'une colonne de cases. */
function DayHead({
  day,
  isToday,
  className,
}: {
  day: string;
  isToday: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'chiffre flex flex-col items-center gap-0.5 text-xs font-normal',
        isToday ? 'text-now' : 'text-muted',
        className
      )}
    >
      <span>{dayOfWeekShort(day).slice(0, 1)}</span>
      <span>{dayOfMonth(day)}</span>
    </div>
  );
}

function DayActions({
  done,
  total,
  busy,
  mobile,
  onClick,
}: {
  done: number;
  total: number;
  busy: boolean;
  mobile: boolean;
  onClick: () => void;
}): React.ReactElement {
  const allDone = total > 0 && done === total;
  const remaining = total - done;
  return (
    <div className="flex items-center gap-2 shrink-0">
      <span aria-live="polite" className={cn('chiffre text-sm', allDone ? 'text-success' : 'text-muted')}>
        {done}/{total}
      </span>
      <Button
        variant={mobile ? 'secondary' : 'ghost'}
        size={mobile ? 'default' : 'small'}
        disabled={busy}
        onClick={onClick}
        aria-label={
          allDone
            ? `Uncheck all ${total} habits for today`
            : `Check the ${remaining} remaining habit${remaining === 1 ? '' : 's'} for today`
        }
      >
        {allDone ? 'Uncheck all' : 'Check all'}
      </Button>
    </div>
  );
}

// Les largeurs de HabitRow, écrites une fois : la poignée (2rem), la vignette (2.25rem), le nom
// (9rem) et deux écarts de 0.5rem — c'est ce que l'en-tête des jours laisse à sa gauche. Une
// ligne entière fait 36.5rem (584 px) : sous `max-w-xl` elle débordait, et `flex-shrink`
// écrasait les deux boutons icône — l'en-tête des jours glissait alors de 14 px. Les boutons
// sont en `shrink-0` pour qu'un débordement se voie au lieu de désaligner en silence.
const LEAD = 'w-[14.25rem]';
const CELL = { '--case-taille': '1.75rem' } as React.CSSProperties;

function DaysHeader({
  days,
  today,
  children,
}: {
  days: string[];
  today: string;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-end gap-2 px-2">
      <div className={cn(LEAD, 'shrink-0 flex items-center')}>{children}</div>
      <div className="flex gap-1 shrink-0">
        {days.map((d) => (
          <DayHead key={d} day={d} isToday={d === today} className="w-7" />
        ))}
      </div>
      <div className="mention w-16 shrink-0 text-right">streak</div>
    </div>
  );
}

function HabitRow({
  habit,
  days,
  today,
  isDone,
}: {
  habit: Habit;
  days: string[];
  today: string;
  isDone: (hid: string, date: string) => boolean;
}): React.ReactElement {
  const toggle = useHabits((s) => s.toggle);
  const logs = useHabits((s) => s.logs);
  const openHabitForm = useUi((s) => s.openHabitForm);
  const openHabitStats = useUi((s) => s.openHabitStats);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: habit.habitId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...teinte(habit.color ?? DEFAULT_HABIT_COLOR),
    ...CELL,
  };

  const streak = currentStreak(logs, habit.habitId, today);
  const missStreak =
    streak > 0 ? 0 : currentMissStreak(logs, habit.habitId, habit.createdAt, today);

  return (
    <div ref={setNodeRef} style={style} className="ligne gap-2 px-2 hover:bg-surface-muted">
      <Button
        {...listeners}
        {...attributes}
        variant="quiet"
        size="icon-small"
        aria-label={`Drag to reorder ${habit.name}`}
        className="ligne-action shrink-0 touch-none cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={16} aria-hidden />
      </Button>

      <button
        type="button"
        onClick={() => openHabitStats(habit.habitId)}
        aria-label={`${habit.name} stats`}
        className="flex items-center gap-2 w-[11.75rem] shrink-0 min-w-0 text-left"
      >
        <HabitGlyph habit={habit} small />
        <span className="truncate hover:underline">{habit.name}</span>
      </button>

      <div className="flex gap-1 shrink-0">
        {days.map((d) => {
          const done = isDone(habit.habitId, d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggle(habit.habitId, d)}
              className="case"
              aria-pressed={done}
              // Un jour à venir ne se coche pas : la case est vide, en pointillé (kit 3.6.0).
              disabled={d > today}
              aria-current={d === today ? 'date' : undefined}
              aria-label={`${d} ${done ? 'done' : 'not done'}`}
            />
          );
        })}
      </div>

      <div className="chiffre shrink-0 w-16 flex items-center justify-end gap-1 text-sm">
        {streak > 0 ? (
          <>
            <Flame size={14} className="text-warning" aria-hidden />
            <span>{streak}</span>
          </>
        ) : missStreak > 0 ? (
          <>
            <Snowflake size={14} className="text-info" aria-hidden />
            <span className="text-muted">{missStreak}</span>
          </>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>

      <Button
        variant="quiet"
        size="icon-small"
        onClick={() => openHabitForm(habit.habitId)}
        aria-label={`Edit ${habit.name}`}
        className="ligne-action shrink-0"
      >
        <Pencil size={14} aria-hidden />
      </Button>
    </div>
  );
}
